// Мелкие переиспользуемые компоненты и форматтеры.

export const mmss = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

/**
 * Дата со временем в UTC. Именно UTC и именно с секундами: по этим отметкам
 * сверяют, когда раннер последний раз приходил и не отвалился ли он прямо
 * сейчас, — а «20 авг. 2026 г.» на такой вопрос не отвечает.
 */
export const humanTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  const month = ['янв', 'фев', 'мар', 'апр', 'мая', 'июн',
    'июл', 'авг', 'сен', 'окт', 'ноя', 'дек'][d.getUTCMonth()];
  return `${d.getUTCDate()} ${month} ${d.getUTCFullYear()}, `
    + `${p(d.getUTCHours())}:${p(d.getUTCMinutes())}:${p(d.getUTCSeconds())} UTC`;
};

/** Сколько прошло: «12 секунд назад». Рядом с точным временем — для беглого взгляда. */
export const ago = (iso) => {
  if (!iso) return '';
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s} с назад`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} мин назад`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h} ч назад` : `${Math.round(h / 24)} дн назад`;
};

export const humanDate = (iso) =>
  new Date(iso).toLocaleDateString('ru', { day: 'numeric', month: 'short', year: 'numeric' });

export const humanDuration = (sec) => {
  if (!sec) return '—';
  const m = Math.round(sec / 60);
  return m < 1 ? '<1 мин' : `${m} мин`;
};

/** Цвет оценки: от красного к зелёному. Используется и в списках, и в графике. */
/**
 * Русское склонение по числу: 1 спорное место, 2 спорных места, 5 спорных мест.
 * Без него на экране оказывалось «1 спорных мест».
 */
export const plural = (n, one, few, many) => {
  const a = Math.abs(n) % 100;
  const b = a % 10;
  if (a > 10 && a < 20) return many;
  if (b > 1 && b < 5) return few;
  if (b === 1) return one;
  return many;
};

/**
 * Секунда собеса → секунда записи. В записи только речь: час разговора даёт
 * минут тридцать звука, потому что пауз, пока человек думает и читает вопрос,
 * там нет. marks сохраняются при склейке; пустые — записи старые, для них
 * шкалы совпадают (как и было раньше, то есть неверно, но лучше уже никак).
 *
 * @param {Array<{at_s:number, pos_s:number}>} marks
 * @param {number} at секунда собеса
 */
export const toRecording = (marks, at) => {
  if (!marks || !marks.length) return at;
  let pos = 0;
  for (const m of marks) {
    if (m.at_s > at) break;
    pos = m.pos_s;
  }
  return pos;
};

export const scoreColor = (score) =>
  ['#b6403f', '#c96a3f', '#c9a227', '#7ba85a', '#4a9d6a'][Math.min(4, Math.max(0, score - 1))];

/**
 * Очень маленький markdown: разбор приходит текстом с заголовками, списками и
 * жирным. Полноценный парсер сюда тащить незачем, а сырой текст читать больно.
 */
export function renderMarkdown(src) {
  const esc = (s) => s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));
  const inline = (s) => esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/`([^`]+)`/g, '<code>$1</code>');

  const out = [];
  let list = null;
  // Блоки кода нужны с тех пор, как появились форматы со сценарием: артефакт
  // на ревью и вывод команд в разборе инцидента без моноширинного блока
  // нечитаемы, а отступы в них значимы.
  let fence = null;

  const closeList = () => { if (list) { out.push(`</${list}>`); list = null; } };

  for (const raw of String(src || '').split('\n')) {
    const line = raw.trimEnd();

    const fenceMark = /^\s*(```|~~~)\s*([\w-]*)\s*$/.exec(line);
    if (fence) {
      if (fenceMark) { out.push(esc(fence.join('\n')), '</code></pre>'); fence = null; }
      else fence.push(raw);
      continue;
    }
    if (fenceMark) { closeList(); out.push(`<pre><code>`); fence = []; continue; }
    const heading = /^(#{1,4})\s+(.*)$/.exec(line);
    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^(\d+)[.)]\s+(.*)$/.exec(line);

    if (heading) {
      closeList();
      const level = Math.min(4, heading[1].length + 1);
      out.push(`<h${level}>${inline(heading[2])}</h${level}>`);
    } else if (bullet) {
      if (list !== 'ul') { closeList(); out.push('<ul>'); list = 'ul'; }
      out.push(`<li>${inline(bullet[1])}</li>`);
    } else if (numbered) {
      if (list !== 'ol') { closeList(); out.push('<ol>'); list = 'ol'; }
      out.push(`<li>${inline(numbered[2])}</li>`);
    } else if (!line.trim()) {
      closeList();
    } else {
      closeList();
      out.push(`<p>${inline(line)}</p>`);
    }
  }
  // Блок кода не закрыли — вывести всё равно надо, иначе текст пропадёт молча.
  if (fence) out.push(esc(fence.join('\n')), '</code></pre>');
  closeList();
  return out.join('');
}

export const Markdown = {
  props: { text: String },
  computed: { html() { return renderMarkdown(this.text); } },
  template: `<div class="md" v-html="html"></div>`,
};

export const Empty = {
  props: { title: String, hint: String },
  template: `
    <div class="empty">
      <h2>{{ title }}</h2>
      <p class="muted">{{ hint }}</p>
      <slot/>
    </div>`,
};
